import React, { useState, useEffect } from "react";
import { BlobServiceClient } from "@azure/storage-blob";
import "./AzureBlobModal.scss";

interface AzureBlobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (selectedFiles: File[]) => void;
  documentType?: "engineering" | "contract" | "invoice" | "others"; // Add document type prop
}

interface FileItem {
  name: string;
  path: string;
  type: "file" | "folder";
  size?: number;
  children?: FileItem[];
}

// Azure Blob Storage configuration
const storageAccountUrl = "https://ogaidtstr01.blob.core.windows.net";
const containerName = "fileuploader";
const sasToken =
  "sp=rl&st=2025-10-24T12:19:10Z&se=2026-11-30T20:34:10Z&spr=https&sv=2024-11-04&sr=c&sig=SdDy5r1myRmjUwDoL7dkamEiYOFfsFvYfTyeMHTUq7I%3D";

const AzureBlobModal: React.FC<AzureBlobModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  documentType = "contract" // Default to contract if not specified
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBlobFiles, setSelectedBlobFiles] = useState<string[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<{
    [path: string]: boolean;
  }>({});
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadBlobFiles();
    }
  }, [isOpen, documentType]); // Add documentType as dependency

  const loadBlobFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const blobServiceClient = new BlobServiceClient(
        `${storageAccountUrl}?${sasToken}`
      );
      const containerClient =
        blobServiceClient.getContainerClient(containerName);

      const blobNames: string[] = [];
      const blobDetails: {
        [key: string]: { size: number; lastModified: Date };
      } = {};

      for await (const blob of containerClient.listBlobsFlat()) {
        blobNames.push(blob.name);
        blobDetails[blob.name] = {
          size: blob.properties.contentLength || 0,
          lastModified: blob.properties.lastModified || new Date()
        };
      }

      const folderTree = buildFolderTree(blobNames, blobDetails);
      const fileItems = convertTreeToFileItems(folderTree);

      setFiles(fileItems);
    } catch (loadError) {
      console.error("Failed to load blob files:", loadError);
      setError(
        "Failed to connect to Azure Blob Storage. Please check your connection."
      );
    } finally {
      setLoading(false);
    }
  };

  const buildFolderTree = (
    blobPaths: string[],
    blobDetails?: { [key: string]: { size: number; lastModified: Date } }
  ) => {
    const tree: any = {};
    blobPaths.forEach((path) => {
      const parts = path.split("/");
      let cur = tree;
      parts.forEach((part, idx) => {
        if (part.trim() === "") return;
        if (idx === parts.length - 1 && !path.endsWith("/")) {
          cur[part] = {
            type: "file",
            size: blobDetails?.[path]?.size || 0,
            lastModified: blobDetails?.[path]?.lastModified
          };
        } else {
          cur[part] = cur[part] || {};
          cur = cur[part];
        }
      });
    });
    return tree;
  };

  const convertTreeToFileItems = (node: any, prefix = ""): FileItem[] => {
    const items: FileItem[] = [];

    Object.entries(node).forEach(([key, val]) => {
      if (val && typeof val === "object" && (val as any).type === "file") {
        items.push({
          name: key,
          path: prefix + key,
          type: "file",
          size: (val as any).size
        });
      } else if (val && typeof val === "object") {
        const folderPath = prefix + key;
        const children = convertTreeToFileItems(val as any, folderPath + "/");
        items.push({
          name: key,
          path: folderPath,
          type: "folder",
          children: children
        });
      }
    });

    return items;
  };

  // Filter folders based on document type
  const filterFoldersByDocumentType = (items: FileItem[]): FileItem[] => {
    return items.filter((item) => {
      if (item.type === "file") {
        return true; // Always show files
      } else if (item.type === "folder") {
        const folderName = item.name.toLowerCase();

        if (documentType === "engineering") {
          // For engineering documents, only show folders containing "engineering" or "engg"
          return (
            folderName.includes("engineering") || folderName.includes("engg")
          );
        } else if (documentType === "invoice") {
          // For invoice documents, only show folders containing "invoice"
          return folderName.includes("invoice");
        } else if (documentType === "others") {
          // For others documents, show folders that don't match specific document types
          return (
            !folderName.includes("engineering") &&
            !folderName.includes("engg") &&
            !folderName.includes("invoice") &&
            !folderName.includes("contract")
          );
        } else {
          // For contract documents, only show folders containing "contract"
          // but exclude engineering and invoice folders
          return (
            folderName.includes("contract") &&
            !folderName.includes("engineering") &&
            !folderName.includes("engg") &&
            !folderName.includes("invoice")
          );
        }
      }
      return false;
    });
  };

  const filteredFiles = filterFoldersByDocumentType(files).filter((file) =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleFolder = (path: string) => {
    const folderKey = path + "/";
    setExpandedFolders((prev) => ({
      ...prev,
      [folderKey]: !prev[folderKey]
    }));
  };

  const toggleFileSelection = (path: string) => {
    setSelectedBlobFiles((prev) => {
      if (prev.includes(path)) {
        const newSelection = prev.filter((p) => p !== path);
        return newSelection;
      } else {
        const newSelection = [...prev, path];
        return newSelection;
      }
    });
  };

  const getAllFilesInFolder = (folderPath: string): string[] => {
    const normalizedPath = folderPath.endsWith("/")
      ? folderPath
      : folderPath + "/";
    const allFilesList: string[] = [];

    const extractFiles = (items: FileItem[]) => {
      items.forEach((item) => {
        if (item.type === "file") {
          allFilesList.push(item.path);
        } else if (item.children) {
          extractFiles(item.children);
        }
      });
    };

    extractFiles(files);
    return allFilesList.filter((file: string) =>
      file.startsWith(normalizedPath)
    );
  };

  const toggleFolderSelection = (folderPath: string) => {
    const folderFiles = getAllFilesInFolder(folderPath);

    setSelectedBlobFiles((prev) => {
      const allSelected = folderFiles.every((file) => prev.includes(file));

      if (allSelected) {
        const newSelection = prev.filter((file) => !folderFiles.includes(file));
        return newSelection;
      } else {
        const newSelection = [...new Set([...prev, ...folderFiles])];
        return newSelection;
      }
    });
  };

  const isFolderSelected = (folderItem: FileItem): boolean => {
    const folderFiles = getAllFilesInFolder(folderItem.path);
    return (
      folderFiles.length > 0 &&
      folderFiles.every((file) => selectedBlobFiles.includes(file))
    );
  };

  const renderTree = (items: FileItem[], level = 0) => {
    return items.map((item) => (
      <div
        key={item.path}
        className="tree-item"
        style={{ paddingLeft: `${level * 20}px` }}
      >
        <div className="item-row">
          {item.type === "folder" ? (
            <div className="folder-item">
              <input
                type="checkbox"
                checked={isFolderSelected(item)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleFolderSelection(item.path);
                }}
                className="folder-checkbox"
              />
              <span
                className="expand-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(item.path);
                }}
              >
                {expandedFolders[item.path + "/"] ? "▼" : "▶"}
              </span>
              <span className="folder-icon">📁</span>
              <span
                className="item-name"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolderSelection(item.path);
                }}
              >
                {item.name}
              </span>
            </div>
          ) : (
            <div
              className="file-item"
              onClick={(e) => {
                if (
                  e.target !== e.currentTarget.querySelector(".file-checkbox")
                ) {
                  toggleFileSelection(item.path);
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedBlobFiles.includes(item.path)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleFileSelection(item.path);
                }}
                className="file-checkbox"
              />
              <span className="file-icon">📄</span>
              <span className="item-name">{item.name}</span>
              {item.size && (
                <span className="file-size">
                  {(item.size / 1024).toFixed(1)} KB
                </span>
              )}
            </div>
          )}
        </div>
        {item.type === "folder" &&
          expandedFolders[item.path + "/"] &&
          item.children &&
          renderTree(item.children, level + 1)}
      </div>
    ));
  };

  const handleSendBlobUrls = async () => {
    if (selectedBlobFiles.length === 0) {
      return;
    }

    try {
      const blobServiceClient = new BlobServiceClient(
        `${storageAccountUrl}?${sasToken}`
      );
      const containerClient =
        blobServiceClient.getContainerClient(containerName);

      const blobFiles: File[] = [];

      for (const filePath of selectedBlobFiles) {
        const fileName = filePath.split("/").pop() || filePath;
        const blobClient = containerClient.getBlobClient(filePath);

        // Download the actual blob content
        const downloadResponse = await blobClient.download();
        const blobBody = await downloadResponse.blobBody;
        const blobArrayBuffer = await blobBody?.arrayBuffer();

        if (blobArrayBuffer) {
          // Create a proper File object with the actual content
          const file = new File([blobArrayBuffer], fileName);

          // Add Azure blob metadata
          (file as any).isAzureBlob = true;
          (
            file as any
          ).blobUrl = `${storageAccountUrl}/${containerName}/${filePath}?${sasToken}`;
          (file as any).blobPath = filePath;

          blobFiles.push(file);
        }
      }

      onUpload(blobFiles);
      handleClose();
    } catch (downloadError) {
      console.error("Error downloading blob files:", downloadError);
      setError("Failed to download selected files from Azure Blob Storage.");
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    setSelectedBlobFiles([]);
    setExpandedFolders({});
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="azure-blob-modal-overlay">
      <div className="azure-blob-modal">
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>Azure Container Browser</h2>
          </div>
          <button type="button" className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        {/* Search */}
        <div className="search-section">
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search files or folders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="divider"></div>

        {/* File Browser */}
        <div className="file-browser">
          {loading ? (
            <div className="no-files">
              Loading files from Azure Blob Storage...
            </div>
          ) : error ? (
            <div className="no-files error-message">{error}</div>
          ) : filteredFiles.length === 0 ? (
            <div className="no-files">No files found.</div>
          ) : (
            <div className="file-tree">{renderTree(filteredFiles)}</div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="modal-actions">
          <div className="action-buttons">
            <button
              type="button"
              className="upload-button"
              onClick={handleSendBlobUrls}
              disabled={selectedBlobFiles.length === 0}
            >
              Upload Selected ({selectedBlobFiles.length})
            </button>
            <button
              type="button"
              className="clear-button"
              onClick={() => setSelectedBlobFiles([])}
              disabled={selectedBlobFiles.length === 0}
            >
              Clear All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AzureBlobModal;
